import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Filter,
  X,
  Eye,
  Ban,
  CheckCircle,
} from 'lucide-react';
import { getVenueById } from '../services/venueService';
import { getVenueBookings, cancelBooking, checkInPlayer } from '../services/bookingService';
import {
  formatDate,
  formatTime,
  formatCurrency,
  getStatusBadgeClass,
} from '../utils/formatters';
import { getBookingStatusLabel } from '../utils/bookingStatus';

export default function VenueBookings() {
  const { id: venueId } = useParams();
  const navigate = useNavigate();

  const [venue, setVenue] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [activePreset, setActivePreset] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  function applyPreset(preset) {
    const today = new Date();
    const toISODate = (d) => d.toISOString().split('T')[0];

    let from, to;
    if (preset === 'today') {
      from = to = today;
    } else if (preset === 'week') {
      const day = today.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      from = new Date(today);
      from.setDate(today.getDate() + diff);
      to = new Date(from);
      to.setDate(from.getDate() + 6);
    } else if (preset === 'month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }

    setActivePreset(preset);
    setFromDate(toISODate(from));
    setToDate(toISODate(to));
  }

  const loadBookings = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      params.status = statusFilter === 'confirmed' ? 'active' : statusFilter;
      const [venueData, bookingsData] = await Promise.all([
        getVenueById(venueId),
        getVenueBookings(venueId, params),
      ]);
      setVenue(venueData.venue || venueData);
      const loadedBookings = Array.isArray(bookingsData.bookings) ? bookingsData.bookings : [];
      setBookings(statusFilter === 'confirmed'
        ? loadedBookings.filter((booking) => ['confirmed', 'pending'].includes(String(booking.status || '').toLowerCase()))
        : loadedBookings);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [venueId, fromDate, toDate, statusFilter]);

  useEffect(() => {
    setLoading(true);
    loadBookings();
  }, [loadBookings]);

  async function handleCancelBooking(bookingId) {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    try {
      await cancelBooking(bookingId, cancelReason);
      toast.success('Booking cancelled');
      setCancelModal(null);
      setCancelReason('');
      loadBookings();
    } catch (err) {
      toast.error('Failed to cancel booking');
    }
  }

  async function handleCheckIn(bookingId) {
    try {
      await checkInPlayer(bookingId);
      toast.success('Player checked in');
      loadBookings();
    } catch (err) {
      toast.error('Failed to check in player');
    }
  }

  if (loading && !bookings.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-secondary">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Bookings</h1>
          <p className="text-sm text-[#64748B]">
            {venue?.name || 'Venue'} — View and manage bookings
          </p>
        </div>
      </div>

      <div className="card p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#64748B]" />
              <span className="text-sm text-[#64748B]">Filters:</span>
            </div>
            <div className="flex gap-1 bg-[#F9FAFB] p-1 rounded-lg">
              {[
                { key: 'today', label: 'Today' },
                { key: 'week', label: 'This Week' },
                { key: 'month', label: 'This Month' },
              ].map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => applyPreset(preset.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activePreset === preset.key
                      ? 'bg-[#264D59] text-black font-bold'
                      : 'text-[#64748B] hover:text-[#1F2937] hover:bg-[#333]'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#64748B]">From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setActivePreset(null); }}
                className="input-field"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#64748B]">To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setActivePreset(null); }}
                className="input-field"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-field"
              >
                <option value="active">Active bookings</option>
                <option value="all">All statuses</option>
                <option value="confirmed">Confirmed</option>
                <option value="checkedin">Checked In</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {(fromDate || toDate || statusFilter !== 'active' || activePreset) && (
              <button
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                  setStatusFilter('active');
                  setActivePreset(null);
                }}
                className="btn-secondary text-sm"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
      </div>

      <div className="card overflow-hidden">
        {bookings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[#64748B]">No bookings found for the selected filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#2B2B2B]">
              <thead className="bg-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Player</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Court</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#64748B] uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#64748B] uppercase">Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#64748B] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#333]">
                {bookings.map((booking) => {
                  const bookingId = booking._id || booking.id;
                  return (
                    <tr
                      key={bookingId}
                      className="hover:bg-[#F9FAFB] cursor-pointer"
                      onClick={() => navigate(`/bookings/${bookingId}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-[#1F2937]">
                            {booking.playerName || booking.player?.name || 'N/A'}
                          </p>
                          <p className="text-xs text-[#64748B]">
                            {booking.playerEmail || booking.player?.email || ''}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#64748B]">
                        {booking.courtName || booking.court?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#64748B]">
                        {formatDate(booking.startTime || booking.start || booking.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#64748B]">
                        {formatTime(booking.startTime || booking.start)} -{' '}
                        {formatTime(booking.endTime || booking.end)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(booking.status)}`}
                        >
                          {getBookingStatusLabel(booking.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1F2937] text-right font-medium">
                        {formatCurrency(booking.amount || booking.totalAmount || 0)}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/bookings/${bookingId}`)}
                            className="text-[#64748B] hover:text-primary-600"
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {['confirmed', 'pending'].includes(String(booking.status || '').toLowerCase()) && (
                            <button
                              onClick={() => handleCheckIn(bookingId)}
                              className="text-[#64748B] hover:text-green-600"
                              title="Check in player"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                            <button
                              onClick={() => {
                                setCancelModal(bookingId);
                                setCancelReason('');
                              }}
                              className="text-[#64748B] hover:text-red-600"
                              title="Cancel booking"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#264D59] bg-opacity-50 p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#CBD5E1]">
              <h3 className="text-lg font-semibold text-[#1F2937]">Cancel Booking</h3>
              <button
                onClick={() => setCancelModal(null)}
                className="text-[#64748B] hover:text-[#64748B]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#64748B]">
                Are you sure you want to cancel this booking? This action cannot be undone.
              </p>
              <div>
                <label className="block text-sm font-medium text-[#64748B]">
                  Cancellation Reason
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="input-field mt-1"
                  placeholder="Provide a reason for cancellation..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setCancelModal(null)}
                  className="btn-secondary"
                >
                  Keep Booking
                </button>
                <button
                  onClick={() => handleCancelBooking(cancelModal)}
                  className="btn-danger"
                >
                  Cancel Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
